import { commandCenterHref, warpDepart } from '@squadlab/os-chrome';
import type { Container } from './container.js';
import type { Router } from './router.js';
import type { Result } from '../core/result.js';
import { scanEmployees } from '../core/compliance-scan.js';

/**
 * Semantic commands → effects (ADR 0032). The ONE place user intentions are
 * bound to ports/navigation; views only ever `commands.dispatch(...)`.
 *
 * Grows per phase: P3 registers employee commands, P4 case/gate commands, P5
 * leave — each handler owns its effect and emits the versioned domain event
 * when the outcome is domain-significant.
 */
export function wireCommands(container: Container, router: Router): void {
  container.commands.register('nav.home', () => {
    router.navigate('/');
  });
  // Leaving the service entirely: a cross-origin hop to the OS front door, so the
  // router is the wrong tool. The destination is catalogue-derived (never a literal
  // URL) and the warp curtain matches the Command Center's arrival (ADR 0110).
  container.commands.register('nav.commandCenter', () => {
    warpDepart(commandCenterHref());
  });
  container.commands.register('people.open', () => {
    router.navigate('/people');
  });
  container.commands.register('employee.open', ({ employeeId }) => {
    router.navigate(`/people/${encodeURIComponent(employeeId)}`);
  });
  container.commands.register('venture.switch', ({ ventureId }) => {
    // Ignore unknown venture ids (a stale/forged payload must not corrupt session state).
    if (!container.ventures.some((v) => v.id === ventureId)) return;
    if (container.session.getState().ventureId === ventureId) return;
    container.session.update(() => ({ ventureId }));
  });
  container.commands.register('cases.open', () => {
    router.navigate('/cases');
  });
  container.commands.register('case.detail.open', ({ caseId }) => {
    router.navigate(`/cases/${encodeURIComponent(caseId)}`);
  });

  // ── Runner effects: failures surface via runnerActivity (never silent) and
  //    go to telemetry; successes speak through hr.*.v1 events. ──
  const settle = (result: Result<unknown>): void => {
    if (result.ok) {
      container.runnerActivity.update(() => ({ lastError: null }));
      return;
    }
    if (result.error.kind === 'cancelled') return;
    container.telemetry.error(result.error);
    container.runnerActivity.update(() => ({ lastError: result.error }));
  };

  container.commands.register('case.open', ({ playbookKey, employeeId }) => {
    void (async () => {
      const signal = new AbortController().signal;
      const ventureId = container.session.getState().ventureId;
      const playbook = container.playbooks.find((p) => p.key === playbookKey);
      if (!playbook) {
        settle({ ok: false, error: { kind: 'not_found', message: `Unknown playbook ${playbookKey}` } });
        return;
      }
      let employee;
      if (employeeId) {
        const found = await container.employees.get(ventureId, employeeId, signal);
        if (!found.ok) {
          settle(found);
          return;
        }
        employee = found.value;
      }
      settle(await container.runner.open({ playbook, ventureId, ...(employee ? { employee } : {}) }, signal));
    })();
  });

  container.commands.register('locale.switch', ({ locale }) => {
    const url = new URL(window.location.href);
    url.searchParams.set('lang', locale);
    // A clean reload re-bootstraps with the new catalog + direction (P8).
    window.location.assign(url.toString());
  });

  // The watchdog (P6): expiring documents open their own renewal cases —
  // across ALL ventures, deduped so each document instance gets ONE case.
  container.commands.register('compliance.scan', () => {
    void (async () => {
      const signal = new AbortController().signal;
      const playbook = container.playbooks.find((p) => p.key === 'doc-renewal');
      if (!playbook) return;
      for (const venture of container.ventures) {
        const listed = await container.employees.list(venture.id, signal);
        if (!listed.ok) {
          settle(listed);
          continue;
        }
        const hits = scanEmployees(listed.value, container.clock.now());
        for (const hit of hits) {
          if (container.renewalsOpened.getState()[hit.key]) continue;
          const employee = listed.value.find((e) => e.id === hit.employeeId);
          if (!employee) continue;
          const opened = await container.runner.open(
            {
              playbook,
              ventureId: venture.id,
              employee,
              input: {
                kind: hit.kind,
                expiresOn: hit.expiresOn,
                ...(hit.reference ? { reference: hit.reference } : {}),
              },
            },
            signal,
          );
          if (!opened.ok) {
            settle(opened);
            continue;
          }
          const caseId = opened.value.id;
          container.renewalsOpened.update((prev) => ({ ...prev, [hit.key]: caseId }));
        }
      }
    })();
  });

  container.commands.register('leave.request', ({ employeeId, startDate, endDate }) => {
    void (async () => {
      const signal = new AbortController().signal;
      const ventureId = container.session.getState().ventureId;
      const playbook = container.playbooks.find((p) => p.key === 'leave-request');
      if (!playbook) {
        settle({ ok: false, error: { kind: 'not_found', message: 'Leave playbook is not registered' } });
        return;
      }
      const found = await container.employees.get(ventureId, employeeId, signal);
      if (!found.ok) {
        settle(found);
        return;
      }
      settle(
        await container.runner.open(
          { playbook, ventureId, employee: found.value, input: { startDate, endDate } },
          signal,
        ),
      );
    })();
  });

  container.commands.register('letter.request', ({ employeeId, letterKind }) => {
    void (async () => {
      const signal = new AbortController().signal;
      const ventureId = container.session.getState().ventureId;
      const playbook = container.playbooks.find((p) => p.key === 'hr-letter');
      if (!playbook) {
        settle({ ok: false, error: { kind: 'not_found', message: 'Letter playbook is not registered' } });
        return;
      }
      const found = await container.employees.get(ventureId, employeeId, signal);
      if (!found.ok) {
        settle(found);
        return;
      }
      settle(
        await container.runner.open(
          { playbook, ventureId, employee: found.value, input: { kind: letterKind } },
          signal,
        ),
      );
    })();
  });

  const gate = (action: 'approve' | 'redo' | 'reject') =>
    ({ caseId, note }: { caseId: string; note?: string }): void => {
      void (async () => {
        const signal = new AbortController().signal;
        const ventureId = container.session.getState().ventureId;
        settle(await container.runner[action](ventureId, caseId, note, signal));
      })();
    };
  container.commands.register('case.gate.approve', gate('approve'));
  container.commands.register('case.gate.redo', gate('redo'));
  container.commands.register('case.gate.reject', gate('reject'));
}
